import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ANALYTICS_EVENTS } from '@g99-product-web-ui/shared-util-lib';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SKIP_401_HANDLER, SKIP_AUTH } from './ auth-context';
import { AnalyticsProxyService } from '../../services/analytics-service/analytics-proxy.service';
import { SubjectManageService } from '../../services/helper-service/subject-manager.service';
import { ToastService } from '../../services/toast-service/toast.service';

/**
 * List of routes that do not require authentication.
 */
const allowedRoutes: string[] = [
  '/auth',
  '/public/',
  '/publicv1/',
  '/publicv2/',
  'onboarding',
  'api2.amplitude.com',
  'mfa',
  'api.review-manager',
  'api-integrations',
  'api-integration.json',
  '/assets',
  'forgot-password',
  'business-selection',
  'g99rag.gogroth.com',
  // Gia Health Check endpoints - don't trigger logout on 401
  'api/v1/ai-responder/health-check',
  'api/v1/ai-responder/ollama/health',
  'api/v1/ai-responder/lightrag/health',
  'api/v1/ai-responder/rds/health',
  'api/v1/ai-responder/timeout-monitor/health',
  'api/v1/ai-responder/embeddings/health',
];

/**
 * Public, token-based app pages (no Cognito login). When the user is on one of these, a background
 * request that happens to lack an Authorization header must NOT bounce the client to the login screen
 * — the page is meant for unauthenticated clients (e.g. the onboarding wizard opened from a HubSpot link).
 */
const publicPagePrefixes: string[] = ['/form-builder/onboarding/'];

function isOnPublicPage(): boolean {
  return (
    typeof window !== 'undefined' &&
    publicPagePrefixes.some((prefix: string) =>
      window.location.pathname.includes(prefix)
    )
  );
}

/**
 * Authentication interceptor to handle session expiry and unauthorized requests.
 * @param req - The outgoing HTTP request.
 * @param next - The request handler function.
 * @returns An observable of the HTTP event.
 */
export const SessionTimeoutInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const toastService = inject(ToastService);
  const authService = inject(SubjectManageService);
  const analyticsService = inject(AnalyticsProxyService);

  // Skip if context token is set
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }
  // Bypass authentication for allowed routes
  if (isAllowedRoute(req.url)) {
    return next(req);
  }

  // If context says: skip 401 → return request normally
  const skip401 = req.context.get(SKIP_401_HANDLER);

  const token: string | null = req.headers.get('Authorization');
  if (!token) {
    // On a public page, or when the caller opted out of 401 handling, let the
    // (un-authed) request proceed instead of redirecting to login.
    if (isOnPublicPage() || skip401) {
      return next(req);
    }
    return handleUnauthorized(
      router,
      toastService,
      authService,
      analyticsService,
      req
    );
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && skip401) {
        return EMPTY;
      }

      if (error.status === 401) {
        // On a public page, surface the error to the caller but don't force a login redirect.
        if (isOnPublicPage()) {
          return throwError(() => error);
        }
        return handleUnauthorized(
          router,
          toastService,
          authService,
          analyticsService,
          req
        );
      }
      return throwError(() => error);
    })
  );
};

/**
 * Handles unauthorized access by clearing session, displaying an error message, and redirecting to login.
 * @param router - The Angular router instance.
 * @param toastService - The toast notification service.
 * @param authService - The authentication service to handle logout.
 * @param analyticsService - The analytics service.
 * @param req - The request that triggered the 401.
 * @returns An empty observable to terminate the request.
 */
function handleUnauthorized(
  router: Router,
  toastService: ToastService,
  authService: SubjectManageService,
  analyticsService: AnalyticsProxyService,
  req: HttpRequest<unknown>
): Observable<never> {
  try {
    // 1. High-resolution error event
    analyticsService.track(ANALYTICS_EVENTS.AUTH.UNAUTHORIZED_ACCESS, {
      attempted_url: req.url,
      method: req.method,
      reason: '401 Unauthorized',
    });
  } catch (e) {
    console.error('Analytics tracking failed', e);
  }

  toastService.showError('Session expired. Please log in again.');

  // 2. Linked session termination event with duration
  authService.logout(false, 'unauthorized_access');

  router.navigate(['']);
  return EMPTY;
}

/**
 * Checks if the request URL is in the allowed list.
 * @param url - The request URL.
 * @returns True if the URL is in the allowed list, otherwise false.
 */
function isAllowedRoute(url: string): boolean {
  return allowedRoutes.some((route: string) => url.includes(route));
}
